import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Input, Select, Space, Tag, Popconfirm, message, Typography, Switch, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, FileTextOutlined } from "@ant-design/icons";
import templatesService from "../../services/templatesService";
import { useCommon } from '../../context/CommonContext';
const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const TemplateList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle("Templates");
  }, []);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });
  const [filters, setFilters] = useState({ status: "all", search: "" });

  const columns = [
    {
      title: "Template",
      key: "template",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            }}
          >
            <FileTextOutlined style={{ color: "#fff", fontSize: "20px" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 600,
                color: "#262626",
                fontSize: "16px",
                marginBottom: 4,
                lineHeight: 1.2,
              }}
            >
              {record.temp_name}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#8c8c8c",
                fontFamily: "Monaco, Consolas, monospace",
                background: "#f5f5f5",
                padding: "2px 6px",
                borderRadius: 4,
                display: "inline-block",
              }}
            >
              {record.temp_slug}
            </div>
          </div>
        </div>
      ),
      width: 300,
    },
    {
      title: "Details",
      key: "details",
      render: (_, record) => (
        <div style={{ maxWidth: 250 }}>
          <div style={{ marginBottom: 8 }}>
            <Tag
              color="blue"
              style={{
                fontSize: "11px",
                borderRadius: 12,
                fontWeight: 500,
                textTransform: "capitalize",
              }}
            >
              {record.temp_type || "Default"}
            </Tag>
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#595959",
              lineHeight: 1.4,
            }}
          >
            {record.temp_description ? (
              record.temp_description.length > 80 ? (
                `${record.temp_description.substring(0, 80)}...`
              ) : (
                record.temp_description
              )
            ) : (
              <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>
                No description provided
              </span>
            )}
          </div>
        </div>
      ),
      width: 280,
    },
    {
      title: "Status & Date",
      key: "status_date",
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: 8 }}>
            <Switch
              checked={record.is_active}
              onChange={() => handleStatusChange(record.temp_id)}
              size="small"
              style={{
                background: record.is_active ? "#52c41a" : "#d9d9d9",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#8c8c8c",
              fontWeight: 500,
            }}
          >
            {new Date(record.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>
      ),
      width: 120,
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 140,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit Template">
            <Button
              icon={<EditOutlined />}
              size="small"
              type="text"
              onClick={() => navigate(`/templates/edit/${record.temp_id}`)}
              style={{
                color: "#52c41a",
                borderRadius: 6,
                transition: "all 0.2s",
              }}
              className="action-btn"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Template?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.temp_id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete Template">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                type="text"
                danger
                style={{
                  borderRadius: 6,
                  transition: "all 0.2s",
                }}
                className="action-btn"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getData = async () => {
    try {
      setLoading(true);
      const res = await templatesService.getTemplates({
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status,
        search: filters.search,
        sortBy: "created_at",
        sortOrder: "desc",
      });

      if (res?.data?.status) {
        setRows(res.data.data || []);
        const meta = res.data.meta || {};
        setPagination((p) => ({
          ...p,
          total: meta.totalEntries || 0,
          current: meta.currentPage || 1,
        }));
      }
    } catch (e) {
      message.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await templatesService.deleteTemplate(id);
      message.success("Template deleted successfully");
      getData();
    } catch (e) {
      message.error("Failed to delete template");
    }
  };

  const handleStatusChange = async (id) => {
    try {
      await templatesService.changeStatus(id);
      message.success("Template status updated");
      getData();
    } catch (e) {
      message.error("Failed to update template status");
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.status, filters.search]);

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <>
      {/* Simple Header Controls */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Search
          placeholder="Search templates..."
          allowClear
          value={filters.search}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value }))
          }
          onSearch={() => getData()}
          style={{ width: 280 }}
        />
        <Select
          style={{ width: 140 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
        >
          <Option value="all">All Status</Option>
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
        </Select>

        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: "13px" }}>
          {pagination.total} templates
        </Text>
        <Button
          icon={<ReloadOutlined />}
          onClick={getData}
          type="text"
          size="small"
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/templates/add/new")}
          size="large"
          style={{
            borderRadius: 8,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            fontWeight: 500,
          }}
        >
          Add New Template
        </Button>
      </div>

      {/* Beautiful Templates Table */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
        }}
      >
        <Table
          rowKey="temp_id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => (
              <span style={{ color: "#8c8c8c", fontSize: "13px" }}>
                Showing {range[0]}-{range[1]} of {total} templates
              </span>
            ),
            style: { padding: "16px 24px" },
          }}
          rowSelection={{
            ...rowSelection,
            columnWidth: 48,
          }}
          onChange={(pg, filters, sorter) => {
            setPagination((p) => ({
              ...p,
              current: pg.current,
              pageSize: pg.pageSize,
            }));
          }}
          scroll={{ x: 1000 }}
          size="middle"
          rowClassName={(record, index) =>
            `template-row ${index % 2 === 0 ? "even-row" : "odd-row"}`
          }
          style={{
            ".template-row": {
              transition: "all 0.2s ease",
            },
            ".template-row:hover": {
              backgroundColor: "#f8f9ff",
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px rgba(102, 126, 234, 0.1)",
            },
            ".even-row": {
              backgroundColor: "#fafbff",
            },
            ".ant-table-thead > tr > th": {
              backgroundColor: "#f8f9ff",
              borderBottom: "2px solid #e6f7ff",
              fontWeight: 600,
              color: "#262626",
              fontSize: "14px",
            },
            ".action-btn:hover": {
              backgroundColor: "rgba(24, 144, 255, 0.1)",
              transform: "scale(1.1)",
            },
          }}
        />
      </div>
    </>
  );
};

export default TemplateList;
